import { useCallback, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router-dom';
import type { CreateEntityFormState } from '@features/create-entity-modal/types';
import { invalidateSavedViewCounts } from '@queries/fetchSavedViews';
import { createTemplate } from '@src/mutations';
import Nav from '@src/routes';
import { useQueryClient } from '@tanstack/react-query';

interface UseCreateEntityArgs {
  onClose: () => void;
  isTemplate: boolean;
}

export const useCreateEntity = ({ onClose, isTemplate }: UseCreateEntityArgs) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleOpenEntity = useCallback(
    (id: string) => {
      const payload = { id };
      const newPath = isTemplate ? Nav.TemplateContract : Nav.SubmissionForm;
      const nextRoute = generatePath(newPath, payload);

      return navigate(nextRoute);
    },
    [isTemplate, navigate],
  );

  const handleCreate: SubmitHandler<CreateEntityFormState> = useCallback(
    async (data) => {
      try {
        setIsLoading(true);
        const {
          //
          branding,
          branding_id,
          copy_from_submission,
          copy_from_template,
          class_of_business,
          name,
          team,
        } = data;

        const { id } = await createTemplate(isTemplate)({
          branding,
          branding_id,
          class_of_business,
          copy_from_submission,
          copy_from_template,
          name,
          teams: team,
        });

        if (!isTemplate) {
          invalidateSavedViewCounts(queryClient);
        }

        onClose();

        return handleOpenEntity(id);
      } catch (_error) {
        // Swallow the error for now
      } finally {
        setIsLoading(false);
      }
    },
    [handleOpenEntity, isTemplate, onClose, queryClient],
  );

  return {
    isLoading,
    onSubmit: handleCreate,
  };
};
